// Product page enhancement: interactive gallery, quantity steppers, and variant
// sync via the Section Rendering API — the fetched section HTML is rendered by
// Liquid, so price/sale/inventory/button state need no money-formatting in JS.
// Zero dependencies, deferred. No-JS fallback stays intact: the form posts
// natively, thumbnails are plain anchor links to slides, quantity is a number input.
(() => {
  const root = document.querySelector('[data-product-root]');
  if (!root) return;
  const sectionId = root.dataset.sectionId;

  /* ---- Gallery: thumbnail anchors activate a slide ---- */
  const stage = root.querySelector('[data-product-stage]');
  const thumbs = root.querySelectorAll('[data-thumb]');

  const showMedia = (mediaId) => {
    const slide = stage && stage.querySelector(`[data-media="${mediaId}"]`);
    if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    thumbs.forEach((t) => {
      const active = t.dataset.thumb === String(mediaId);
      t.classList.toggle('is-active', active);
      if (active) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  };

  thumbs.forEach((t) => {
    t.addEventListener('click', (e) => {
      e.preventDefault();
      showMedia(t.dataset.thumb);
    });
  });

  /* ---- Deferred 3D / external-video viewers: swap the poster for the real
     viewer (from the slide's <template>) on first interaction. 3D loads Shopify's
     hosted model-viewer-ui on demand — nothing third-party loads eagerly. ---- */
  root.querySelectorAll('[data-media-load]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const slide = btn.closest('[data-media]');
      const tpl = slide && slide.querySelector('template');
      if (!tpl) return;
      const kind = btn.dataset.mediaLoad;
      slide.replaceChildren(tpl.content.cloneNode(true));
      // Hosted video: play() here rather than an autoplay attribute — the click gives us
      // user activation, so it starts WITH sound; a bare autoplay attribute is policy-gated
      // and would leave the merchant's video sitting paused behind its own controls.
      // (external_video is an iframe, so the ?. no-ops and its URL autoplay flag runs.)
      slide.querySelector('video')?.play().catch(() => {});
      if (kind === 'model' && window.Shopify && Shopify.loadFeatures) {
        Shopify.loadFeatures([{
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => {
            const mv = slide.querySelector('model-viewer');
            if (mv) new Shopify.ModelViewerUI(mv);
          },
        }]);
      }
    }, { once: true });
  });

  /* ---- Quantity steppers ---- */
  root.querySelectorAll('[data-quantity]').forEach((wrap) => {
    const input = wrap.querySelector('input');
    if (!input) return;
    wrap.querySelectorAll('[data-qty-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const min = Number(input.min) || 1;
        const max = Number(input.max) || Infinity;
        const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(btn.dataset.qtyStep)));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  /* The buy-now block owns a SECOND product form (payment_button needs a form object),
     so the quantity input — associated with the add-to-cart form — can't reach it. Mirror
     it instead. Delegated, so it survives the section swaps below. */
  const mirrorQty = (e) => {
    if (!e.target.matches('[data-quantity] input')) return;
    root.querySelectorAll('[data-buy-now-qty]').forEach((h) => { h.value = e.target.value; });
  };
  // BOTH events: typing fires `input`, but the +/- steppers above set .value and dispatch
  // `change` — an `input`-only listener misses every button press.
  root.addEventListener('input', mirrorQty);
  root.addEventListener('change', mirrorQty);

  /* ---- Variant sync via Section Rendering API ---- */
  const select = root.querySelector('select[name="id"]');
  if (!select) return;

  // Every match, paired by document order: the meta row used to be one block, and is now
  // four, so a merchant can have two SKU lines or a stock pill in the gallery column.
  // querySelector would refresh the first and leave the rest showing the old variant.
  const swap = (doc, selector) => {
    const next = doc.querySelectorAll(selector);
    const cur = root.querySelectorAll(selector);
    for (let i = 0; i < Math.min(next.length, cur.length); i += 1) cur[i].replaceWith(next[i]);
  };

  select.addEventListener('change', () => {
    const id = select.value;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url);

    fetch(`${window.location.pathname}?variant=${id}&section_id=${sectionId}`)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        swap(doc, '[data-price]');
        swap(doc, '[data-buy-buttons]');
        swap(doc, '[data-buy-now]');
        swap(doc, '[data-product-sku]');
    swap(doc, '[data-product-stock]');
        swap(doc, '[data-stock-counter]');
        swap(doc, '[data-sticky-price]');
        swap(doc, '[data-sticky-variant]');
        const newStage = doc.querySelector('[data-product-stage]');
        if (newStage && newStage.dataset.featuredMedia) showMedia(newStage.dataset.featuredMedia);
      })
      .catch(() => {});
  });
})();
